import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import {
  CleaningProcedureId,
  CleaningFormId,
  CleaningFormSectionId,
  CleaningFieldId,
  CleaningFormFieldId,
  CleaningFieldKindEnum,
  type CleaningField,
  type CleaningFormSection,
  type CleaningFormField,
} from "@brewbase/db/types";
import { trpc } from "@/hooks/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  PlusIcon,
  Trash2Icon,
  GripVerticalIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  HashIcon,
  TypeIcon,
  CheckSquareIcon,
  CalculatorIcon,
  CalendarIcon,
  CalendarDaysIcon,
  PackageIcon,
  CircleIcon,
} from "lucide-react";
import { useState, useEffect, useMemo, useCallback } from "react";
import { toast } from "sonner";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { cn } from "@/lib/utils";

export const Route = createFileRoute(
  "/settings/cleaning-procedures/$procedure_id/forms/$form_id",
)({
  component: RouteComponent,
  params: z.object({
    procedure_id: z.coerce
      .number()
      .transform((val) => val as CleaningProcedureId),
    form_id: z.coerce.number().transform((val) => val as CleaningFormId),
  }),
});

// Field kind icons
const KIND_ICONS: Record<CleaningFieldKindEnum, React.ReactNode> = {
  [CleaningFieldKindEnum.NUMBER]: <HashIcon className="h-3.5 w-3.5" />,
  [CleaningFieldKindEnum.TEXT]: <TypeIcon className="h-3.5 w-3.5" />,
  [CleaningFieldKindEnum.CHECKBOX]: <CheckSquareIcon className="h-3.5 w-3.5" />,
  [CleaningFieldKindEnum.COMPUTED]: <CalculatorIcon className="h-3.5 w-3.5" />,
  [CleaningFieldKindEnum.DATETIME]: <CalendarIcon className="h-3.5 w-3.5" />,
  [CleaningFieldKindEnum.DATE]: <CalendarDaysIcon className="h-3.5 w-3.5" />,
  [CleaningFieldKindEnum.INVENTORY_ITEM]: (
    <PackageIcon className="h-3.5 w-3.5" />
  ),
};

// Types for selected item
type SelectedItem =
  | { type: "section"; id: CleaningFormSectionId }
  | {
      type: "field";
      formFieldId: CleaningFormFieldId;
      sectionId: CleaningFormSectionId;
    }
  | null;

function RouteComponent() {
  const { procedure_id, form_id } = Route.useParams();
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  // State
  const [selectedItem, setSelectedItem] = useState<SelectedItem>(null);
  const [expandedSections, setExpandedSections] = useState<Set<number>>(
    new Set(),
  );

  // Queries
  const { data: form, isLoading: isLoadingForm } =
    trpc.cleaning.forms.getWithStructure.useQuery(form_id);

  const { data: allFields } = trpc.cleaning.fields.list.useQuery({
    procedureId: procedure_id,
    includeArchived: false,
  });

  // Local state for form metadata
  const [localName, setLocalName] = useState("");
  const [localDescription, setLocalDescription] = useState<string | null>(null);
  const [localVersion, setLocalVersion] = useState("");

  useEffect(() => {
    if (form) {
      setLocalName(form.name);
      setLocalDescription(form.description);
      setLocalVersion(form.version);
      // Expand all sections by default
      setExpandedSections(new Set(form.sections.map((s) => s.id as number)));
    }
  }, [form]);

  // Mutations
  const updateFormMutation = trpc.cleaning.forms.update.useMutation({
    onSuccess: () => {
      utils.cleaning.forms.getWithStructure.invalidate(form_id);
      utils.cleaning.forms.list.invalidate({ procedureId: procedure_id });
    },
    onError: (err) => {
      toast.error(`Failed to update: ${err.message}`);
    },
  });

  const createSectionMutation = trpc.cleaning.forms.sections.create.useMutation(
    {
      onSuccess: (data) => {
        toast.success("Section added");
        utils.cleaning.forms.getWithStructure.invalidate(form_id);
        setSelectedItem({ type: "section", id: data.id });
        setExpandedSections((prev) => new Set([...prev, data.id as number]));
      },
      onError: (err) => {
        toast.error(`Failed to add section: ${err.message}`);
      },
    },
  );

  const reorderSectionsMutation =
    trpc.cleaning.forms.sections.reorder.useMutation({
      onSuccess: () => {
        utils.cleaning.forms.getWithStructure.invalidate(form_id);
      },
      onError: (err) => {
        toast.error(`Failed to reorder: ${err.message}`);
      },
    });

  const deleteFormMutation = trpc.cleaning.forms.delete.useMutation({
    onSuccess: () => {
      toast.success("Form deleted");
      utils.cleaning.forms.list.invalidate({ procedureId: procedure_id });
      navigate({
        to: "/settings/cleaning-procedures/$procedure_id",
        params: { procedure_id: procedure_id },
      });
    },
    onError: (err) => {
      toast.error(`Failed to delete: ${err.message}`);
    },
  });

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Handlers
  const handleSaveMetadata = useCallback(() => {
    updateFormMutation.mutate({
      id: form_id,
      name: localName,
      description: localDescription,
      version: localVersion,
    });
  }, [form_id, localName, localDescription, localVersion, updateFormMutation]);

  const handleAddSection = () => {
    const count = (form?.sections.length ?? 0) + 1;
    createSectionMutation.mutate({
      formId: form_id,
      name: `Section ${count}`,
    });
  };

  const handleSectionDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !form?.sections) return;

    const oldIndex = form.sections.findIndex((s) => s.id === active.id);
    const newIndex = form.sections.findIndex((s) => s.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = [...form.sections];
    const [removed] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, removed);

    reorderSectionsMutation.mutate({
      formId: form_id,
      sectionIds: reordered.map((s) => s.id),
    });
  };

  const toggleSection = (sectionId: number) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  // Get available fields (not already in form)
  const usedFieldIds = useMemo(() => {
    return new Set(
      form?.sections.flatMap((s) => s.fields.map((f) => f.field_id)) ?? [],
    );
  }, [form?.sections]);

  const availableFields = useMemo(() => {
    return allFields?.filter((f) => !usedFieldIds.has(f.id)) ?? [];
  }, [allFields, usedFieldIds]);

  // Get selected section data
  const selectedSection = useMemo(() => {
    if (selectedItem?.type !== "section") return null;
    return form?.sections.find((s) => s.id === selectedItem.id) ?? null;
  }, [form?.sections, selectedItem]);

  // Get selected form field data
  const selectedFormField = useMemo(() => {
    if (selectedItem?.type !== "field") return null;
    const section = form?.sections.find((s) => s.id === selectedItem.sectionId);
    return (
      section?.fields.find((f) => f.id === selectedItem.formFieldId) ?? null
    );
  }, [form?.sections, selectedItem]);

  if (isLoadingForm) {
    return <div className="p-4">Loading form...</div>;
  }

  if (!form) {
    return <div className="p-4 text-destructive">Form not found</div>;
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Compact Header */}
      <div className="flex items-center gap-3 px-4 py-2 border-b flex-shrink-0">
        <Input
          value={localName}
          onChange={(e) => setLocalName(e.target.value)}
          onBlur={handleSaveMetadata}
          className="w-48 text-sm font-medium"
          placeholder="Form name"
        />
        <div className="flex items-center gap-1 text-muted-foreground">
          <span>v</span>
          <Input
            value={localVersion}
            onChange={(e) => setLocalVersion(e.target.value)}
            onBlur={handleSaveMetadata}
            className="w-16 text-xs"
            placeholder="1.0"
          />
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant={form.is_active ? "secondary" : "outline"}
            size="sm"
            aria-pressed={form.is_active}
            className={
              "text-xs flex items-center px-3 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
            }
            onClick={() =>
              updateFormMutation.mutate({
                id: form_id,
                isActive: !form.is_active,
              })
            }
            data-state={form.is_active ? "on" : "off"}
          >
            {form.is_active ? "Active" : "Inactive"}
          </Button>
        </div>
        <div className="flex-1" />
        <Button
          variant="ghost"
          size="sm"
          onClick={() => deleteFormMutation.mutate(form_id)}
          disabled={deleteFormMutation.isPending || form.is_default}
          className="text-destructive hover:text-destructive h-7 text-xs"
        >
          <Trash2Icon className="h-3.5 w-3.5 mr-1" />
          Delete
        </Button>
      </div>

      {/* Main content: List | Inspector */}
      <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-12">
        {/* Left: Sections & Fields List */}
        <div className="md:col-span-5 min-h-0 border-r flex flex-col">
          <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/30">
            <span className="text-xs font-medium text-muted-foreground">
              Sections & Fields
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleAddSection}
              disabled={createSectionMutation.isPending}
              className="h-6 text-xs"
            >
              <PlusIcon className="h-3.5 w-3.5 mr-1" />
              Section
            </Button>
          </div>

          <ScrollArea className="flex-1">
            {form.sections.length > 0 ? (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleSectionDragEnd}
              >
                <SortableContext
                  items={form.sections.map((s) => s.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {form.sections.map((section) => (
                    <SectionItem
                      key={section.id}
                      section={section}
                      formId={form_id}
                      procedureId={procedure_id}
                      isExpanded={expandedSections.has(section.id as number)}
                      onToggle={() => toggleSection(section.id as number)}
                      selectedItem={selectedItem}
                      onSelect={setSelectedItem}
                      availableFields={availableFields}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            ) : (
              <div className="p-4 text-center text-xs text-muted-foreground">
                No sections yet. Add a section to organize fields.
              </div>
            )}
          </ScrollArea>
        </div>

        {/* Right: Inspector Panel */}
        <div className="md:col-span-7 min-h-0">
          {selectedItem ? (
            selectedItem.type === "section" && selectedSection ? (
              <SectionInspector
                section={selectedSection}
                formId={form_id}
                procedureId={procedure_id}
                onClose={() => setSelectedItem(null)}
              />
            ) : selectedItem.type === "field" && selectedFormField ? (
              <FormFieldInspector
                key={selectedFormField.id}
                formField={selectedFormField}
                sectionId={selectedItem.sectionId}
                formId={form_id}
                onClose={() => setSelectedItem(null)}
              />
            ) : (
              <EmptyInspector />
            )
          ) : (
            <EmptyInspector />
          )}
        </div>
      </div>
    </div>
  );
}

// ======================== Section Item ========================

interface SectionItemProps {
  section: CleaningFormSection & {
    fields: (CleaningFormField & { field: CleaningField })[];
  };
  formId: CleaningFormId;
  procedureId: CleaningProcedureId;
  isExpanded: boolean;
  onToggle: () => void;
  selectedItem: SelectedItem;
  onSelect: (item: SelectedItem) => void;
  availableFields: CleaningField[];
}

function SectionItem({
  section,
  formId,
  procedureId,
  isExpanded,
  onToggle,
  selectedItem,
  onSelect,
  availableFields,
}: SectionItemProps) {
  const utils = trpc.useUtils();
  const [isCreatingField, setIsCreatingField] = useState(false);
  const [newFieldName, setNewFieldName] = useState("");
  const [newFieldCode, setNewFieldCode] = useState("");
  const [isCodeManuallyEdited, setIsCodeManuallyEdited] = useState(false);
  const [newFieldKind, setNewFieldKind] = useState<CleaningFieldKindEnum>(
    CleaningFieldKindEnum.TEXT,
  );

  // Auto-generate code from name if not manually edited
  const generateCode = (name: string) =>
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_|_$/g, "");

  const displayCode = isCodeManuallyEdited
    ? newFieldCode
    : generateCode(newFieldName);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const addFieldMutation = trpc.cleaning.forms.fields.create.useMutation({
    onSuccess: (data) => {
      utils.cleaning.forms.getWithStructure.invalidate(formId);
      onSelect({
        type: "field",
        formFieldId: data.id,
        sectionId: section.id,
      });
    },
    onError: (err) => {
      toast.error(`Failed to add field: ${err.message}`);
    },
  });

  const reorderFieldsMutation = trpc.cleaning.forms.fields.reorder.useMutation({
    onSuccess: () => {
      utils.cleaning.forms.getWithStructure.invalidate(formId);
    },
    onError: (err) => {
      toast.error(`Failed to reorder: ${err.message}`);
    },
  });

  // Mutation to create a new record field
  const createFieldMutation = trpc.cleaning.fields.create.useMutation({
    onSuccess: (newField) => {
      // After creating the field, immediately add it to this form section
      utils.cleaning.fields.list.invalidate({ procedureId: procedureId });
      addFieldMutation.mutate({
        formId,
        sectionId: section.id,
        fieldId: newField.id,
      });
      // Reset form
      setIsCreatingField(false);
      setNewFieldName("");
      setNewFieldCode("");
      setIsCodeManuallyEdited(false);
      setNewFieldKind(CleaningFieldKindEnum.TEXT);
    },
    onError: (err) => {
      toast.error(`Failed to create field: ${err.message}`);
    },
  });

  const handleFieldDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = section.fields.findIndex((f) => f.id === active.id);
    const newIndex = section.fields.findIndex((f) => f.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = [...section.fields];
    const [removed] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, removed);

    reorderFieldsMutation.mutate({
      sectionId: section.id,
      fieldIds: reordered.map((f) => f.id),
    });
  };

  const handleAddExistingField = (fieldId: CleaningFieldId) => {
    addFieldMutation.mutate({
      formId,
      sectionId: section.id,
      fieldId,
    });
  };

  const handleCreateNewField = () => {
    if (!newFieldName.trim()) {
      toast.error("Field name is required");
      return;
    }
    const finalCode = isCodeManuallyEdited
      ? newFieldCode.trim()
      : generateCode(newFieldName);
    createFieldMutation.mutate({
      procedureId: procedureId,
      name: newFieldName.trim(),
      code: finalCode,
      kind: newFieldKind,
    });
  };

  const isSelected =
    selectedItem?.type === "section" && selectedItem.id === section.id;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn("border-b border-olive-6", isDragging && "opacity-50")}
    >
      {/* Section header */}
      <div
        className={cn(
          "flex items-center gap-1 px-2 py-1.5 bg-muted/30 cursor-pointer hover:bg-muted/50",
          isSelected && "bg-primary/10",
        )}
        onClick={() => onSelect({ type: "section", id: section.id })}
      >
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab text-muted-foreground hover:text-foreground p-0.5"
          onClick={(e) => e.stopPropagation()}
        >
          <GripVerticalIcon className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          className="text-muted-foreground hover:text-foreground p-0.5"
        >
          {isExpanded ? (
            <ChevronDownIcon className="h-3.5 w-3.5" />
          ) : (
            <ChevronRightIcon className="h-3.5 w-3.5" />
          )}
        </button>
        <span className="text-sm font-medium flex-1 truncate">
          {section.name}
        </span>
        <span className="text-xs text-muted-foreground">
          {section.fields.length} field{section.fields.length !== 1 && "s"}
        </span>
      </div>

      {/* Section fields */}
      {isExpanded && (
        <div className="bg-olive-1">
          {section.fields.length > 0 ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleFieldDragEnd}
            >
              <SortableContext
                items={section.fields.map((f) => f.id)}
                strategy={verticalListSortingStrategy}
              >
                {section.fields.map((formField) => (
                  <FieldItem
                    key={formField.id}
                    formField={formField}
                    sectionId={section.id}
                    isSelected={
                      selectedItem?.type === "field" &&
                      selectedItem.formFieldId === formField.id
                    }
                    onSelect={() =>
                      onSelect({
                        type: "field",
                        formFieldId: formField.id,
                        sectionId: section.id,
                      })
                    }
                  />
                ))}
              </SortableContext>
            </DndContext>
          ) : (
            <div className="px-6 py-2 text-xs text-muted-foreground">
              No fields in this section
            </div>
          )}

          {/* Add field section */}
          <div className="px-2 py-1.5 space-y-2">
            {/* Add existing field dropdown */}
            {availableFields.length > 0 && !isCreatingField && (
              <Select
                onValueChange={(val) => {
                  if (val === "__create_new__") {
                    setIsCreatingField(true);
                  } else {
                    handleAddExistingField(parseInt(val) as CleaningFieldId);
                  }
                }}
              >
                <SelectTrigger className="h-7 text-xs">
                  <SelectValue placeholder="+ Add field..." />
                </SelectTrigger>
                <SelectContent>
                  {availableFields.map((field) => (
                    <SelectItem key={field.id} value={String(field.id)}>
                      <div className="flex items-center gap-2">
                        {KIND_ICONS[field.kind] ?? (
                          <CircleIcon className="h-3.5 w-3.5" />
                        )}
                        <span>{field.name}</span>
                        <span className="text-muted-foreground font-mono text-xs">
                          {field.code}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                  <SelectItem value="__create_new__">
                    <div className="flex items-center gap-2 text-primary">
                      <PlusIcon className="h-3.5 w-3.5" />
                      <span>Create new field...</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            )}

            {/* No existing fields - show create button */}
            {availableFields.length === 0 && !isCreatingField && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsCreatingField(true)}
                className="w-full h-7 text-xs flex justify-start"
              >
                <PlusIcon className="h-3.5 w-3.5 mr-1" />
                Create new field
              </Button>
            )}

            {/* Inline create field form */}
            {isCreatingField && (
              <div className="space-y-2 p-2 bg-muted/30 border border-olive-6">
                <div className="text-xs font-medium">Create New Field</div>
                <Input
                  value={newFieldName}
                  onChange={(e) => setNewFieldName(e.target.value)}
                  placeholder="Field name"
                  className="text-xs"
                  autoFocus
                />
                <div className="flex gap-2">
                  <Input
                    value={displayCode}
                    onChange={(e) => {
                      setNewFieldCode(e.target.value);
                      if (e.target.value.trim()) {
                        setIsCodeManuallyEdited(true);
                      } else {
                        setIsCodeManuallyEdited(false);
                      }
                    }}
                    placeholder="code (auto)"
                    className="text-xs font-mono flex-1"
                  />
                  <Select
                    value={newFieldKind}
                    onValueChange={(val) =>
                      setNewFieldKind(val as CleaningFieldKindEnum)
                    }
                  >
                    <SelectTrigger className="text-xs w-28">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(CleaningFieldKindEnum).map((kind) => (
                        <SelectItem key={kind} value={kind}>
                          <div className="flex items-center gap-1.5">
                            {KIND_ICONS[kind]}
                            <span>{kind.replace(/_/g, " ")}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setIsCreatingField(false);
                      setNewFieldName("");
                      setNewFieldCode("");
                      setIsCodeManuallyEdited(false);
                    }}
                    className="flex-1 h-6 text-xs"
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleCreateNewField}
                    disabled={
                      !newFieldName.trim() || createFieldMutation.isPending
                    }
                    className="flex-1 h-6 text-xs"
                  >
                    {createFieldMutation.isPending ? "Creating..." : "Create"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ======================== Field Item ========================

interface FieldItemProps {
  formField: CleaningFormField & { field: CleaningField };
  sectionId: CleaningFormSectionId;
  isSelected: boolean;
  onSelect: () => void;
}

function FieldItem({ formField, isSelected, onSelect }: FieldItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: formField.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
  } as React.CSSProperties;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-center gap-2 px-2 py-1.5 text-sm cursor-pointer border-b border-olive-6/30 hover:bg-muted/30",
        isSelected && "bg-primary/5 border-l-2 border-l-primary",
      )}
      onClick={onSelect}
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab text-muted-foreground hover:text-foreground p-0.5"
        onClick={(e) => e.stopPropagation()}
      >
        <GripVerticalIcon className="h-3 w-3" />
      </button>
      <span className="text-muted-foreground">
        {KIND_ICONS[formField.field.kind] ?? (
          <CircleIcon className="h-3.5 w-3.5" />
        )}
      </span>
      <span className="flex-1 truncate">{formField.field.name}</span>
      {formField.field.is_optional && (
        <span className="text-xs bg-primary/10 text-primary px-1 py-0.5">
          Optional
        </span>
      )}
    </div>
  );
}

// ======================== Empty Inspector ========================

function EmptyInspector() {
  return (
    <div className="h-full flex items-center justify-center text-xs text-muted-foreground bg-muted/20">
      Select a section or field to edit
    </div>
  );
}

// ======================== Section Inspector ========================

interface SectionInspectorProps {
  section: CleaningFormSection & {
    fields: (CleaningFormField & { field: CleaningField })[];
  };
  formId: CleaningFormId;
  procedureId: CleaningProcedureId;
  onClose: () => void;
}

function SectionInspector({
  section,
  formId,
  procedureId,
  onClose,
}: SectionInspectorProps) {
  const utils = trpc.useUtils();
  const [localName, setLocalName] = useState(section.name);
  const [localDescription, setLocalDescription] = useState(
    section.description ?? "",
  );

  useEffect(() => {
    setLocalName(section.name);
    setLocalDescription(section.description ?? "");
  }, [section]);

  const updateMutation = trpc.cleaning.forms.sections.update.useMutation({
    onSuccess: () => {
      utils.cleaning.forms.getWithStructure.invalidate(formId);
    },
    onError: (err) => {
      toast.error(`Failed to update: ${err.message}`);
    },
  });

  const deleteMutation = trpc.cleaning.forms.sections.delete.useMutation({
    onSuccess: () => {
      toast.success("Section deleted");
      utils.cleaning.forms.getWithStructure.invalidate(formId);
      onClose();
    },
    onError: (err) => {
      toast.error(`Failed to delete: ${err.message}`);
    },
  });

  const handleSave = () => {
    updateMutation.mutate({
      id: section.id,
      name: localName,
      description: localDescription || null,
    });
  };

  return (
    <ScrollArea className="h-full">
      <div className="p-4 space-y-4">
        <div className="flex items-center justify-between">
          <Label className="text-xs uppercase text-muted-foreground font-medium">
            Section
          </Label>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => deleteMutation.mutate(section.id)}
            disabled={deleteMutation.isPending}
            className="h-6 text-xs text-destructive hover:text-destructive"
          >
            <Trash2Icon className="h-3 w-3 mr-1" />
            Delete
          </Button>
        </div>

        <div className="space-y-1">
          <Label className="text-xs">Name</Label>
          <Input
            value={localName}
            onChange={(e) => setLocalName(e.target.value)}
            onBlur={handleSave}
          />
        </div>

        <div className="space-y-1">
          <Label className="text-xs">Description</Label>
          <Input
            value={localDescription}
            onChange={(e) => setLocalDescription(e.target.value)}
            onBlur={handleSave}
            placeholder="Optional section description..."
          />
        </div>

        <div className="border-t pt-4">
          <Label className="text-xs uppercase text-muted-foreground font-medium">
            Fields ({section.fields.length})
          </Label>
          {section.fields.length > 0 ? (
            <div className="mt-2 space-y-1">
              {section.fields.map((f) => (
                <div
                  key={f.id}
                  className="flex items-center gap-2 px-2 py-1.5 text-sm bg-muted/30"
                >
                  {KIND_ICONS[f.field.kind] ?? (
                    <CircleIcon className="h-3.5 w-3.5" />
                  )}
                  <span>{f.field.name}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-2 text-xs text-muted-foreground">
              No fields in this section yet
            </p>
          )}
        </div>
      </div>
    </ScrollArea>
  );
}

// ======================== Form Field Inspector ========================

interface FormFieldInspectorProps {
  formField: CleaningFormField & { field: CleaningField };
  sectionId: CleaningFormSectionId;
  formId: CleaningFormId;
  onClose: () => void;
}

function FormFieldInspector({
  formField,
  formId,
  onClose,
}: FormFieldInspectorProps) {
  const utils = trpc.useUtils();
  const field = formField.field;

  // Form-specific display overrides. The field *definition* (name/kind/options)
  // is edited in the Fields tab now — this inspector only governs how the field
  // appears within this form, so the two editing surfaces don't drift.
  const [localHelp, setLocalHelp] = useState(formField.help_text ?? "");
  const [localPlaceholder, setLocalPlaceholder] = useState(
    formField.placeholder ?? "",
  );

  const updateFormFieldMutation = trpc.cleaning.forms.fields.update.useMutation(
    {
      onSuccess: () => {
        utils.cleaning.forms.getWithStructure.invalidate(formId);
      },
      onError: (err) => {
        toast.error(`Failed to update: ${err.message}`);
      },
    },
  );

  const removeMutation = trpc.cleaning.forms.fields.delete.useMutation({
    onSuccess: () => {
      toast.success("Field removed from form");
      utils.cleaning.forms.getWithStructure.invalidate(formId);
      onClose();
    },
    onError: (err) => {
      toast.error(`Failed to remove: ${err.message}`);
    },
  });

  const handleSaveFormField = () => {
    updateFormFieldMutation.mutate({
      id: formField.id,
      helpText: localHelp || null,
      placeholder: localPlaceholder || null,
    });
  };

  return (
    <ScrollArea className="h-full">
      <div className="p-4 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {KIND_ICONS[field.kind] ?? <CircleIcon className="h-4 w-4" />}
            <Label className="text-xs uppercase text-muted-foreground font-medium">
              Field
            </Label>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => removeMutation.mutate(formField.id)}
            disabled={removeMutation.isPending}
            className="h-6 text-xs text-destructive hover:text-destructive"
          >
            <Trash2Icon className="h-3 w-3 mr-1" />
            Remove
          </Button>
        </div>

        {/* Read-only definition summary — edited in the Fields tab */}
        <div className="space-y-1">
          <div className="text-sm font-medium">{field.name || "Untitled"}</div>
          <div className="text-xs text-muted-foreground font-mono">
            {field.code}
          </div>
          <div className="text-xs text-muted-foreground">
            {field.kind.replace(/_/g, " ").toLowerCase()} · edit this field's
            definition in the Fields tab
          </div>
        </div>

        {/* Form-specific overrides */}
        <div className="border-t pt-4 space-y-3">
          <Label className="text-xs uppercase text-muted-foreground font-medium">
            Form Display Options
          </Label>

          <div className="space-y-1">
            <Label className="text-xs">Placeholder</Label>
            <Input
              value={localPlaceholder}
              onChange={(e) => setLocalPlaceholder(e.target.value)}
              onBlur={handleSaveFormField}
              placeholder="Optional placeholder text..."
            />
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Help Text</Label>
            <Input
              value={localHelp}
              onChange={(e) => setLocalHelp(e.target.value)}
              onBlur={handleSaveFormField}
              placeholder="Optional help text..."
            />
          </div>
          <p className="text-xs text-muted-foreground">
            These options only affect how the field appears in this form
          </p>
        </div>
      </div>
    </ScrollArea>
  );
}
